import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Modal,
  Chip,
} from "@mui/material";
import { Add, Delete, Edit, Close } from "@mui/icons-material";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const Features = ({ groupItems = [], onChange }) => {
  const [openModal, setOpenModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null); // track edit index
  const [newItem, setNewItem] = useState({
    title: "",
    description: "",
    treatments: [],
  });
   const sliderRef = useRef(null);
  const [newTreatment, setNewTreatment] = useState("");

    useEffect(() => {
      if (groupItems.length > 0 && sliderRef.current) {
        // Scroll to last slide
        sliderRef.current.slickGoTo(groupItems.length - 1);
      }
    }, [groupItems]);

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 600, settings: { slidesToShow: 1 } },
    ],
  };

  const handleAddTreatment = () => {
    if (newTreatment.trim()) {
      setNewItem({
        ...newItem,
        treatments: [...newItem.treatments, newTreatment.trim()],
      });
      setNewTreatment("");
    }
  };

  const handleRemoveTreatment = (index) => {
    const updated = [...newItem.treatments];
    updated.splice(index, 1);
    setNewItem({ ...newItem, treatments: updated });
  };

  const handleSaveItem = () => {
    let updated = [...groupItems];

    if (currentIndex !== null) {
      // Edit mode
      updated[currentIndex] = newItem;
    } else {
      // Add mode
      updated.push(newItem);
    }

    onChange(updated);
    handleClose();
  };

  const handleDeleteItem = (index) => {
    const updated = groupItems.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleEditItem = (index) => {
    setCurrentIndex(index);
    setNewItem(groupItems[index]);
    setOpenModal(true);
  };

  const handleClose = () => {
    setOpenModal(false);
    setNewItem({ title: "", description: "", treatments: [] });
    setNewTreatment("");
    setCurrentIndex(null);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ py: 2 }}>
        <Slider ref={sliderRef} {...sliderSettings}>
          {groupItems.map((item, index) => (
            <div key={index}>
              <Paper sx={{ mx: 1, p: 2, position: "relative" }}>
                <IconButton
                  onClick={() => handleDeleteItem(index)}
                  sx={{ position: "absolute", top: 8, right: 8 }}
                >
                  <Delete color="error" />
                </IconButton>

                <IconButton
                  onClick={() => handleEditItem(index)}
                  sx={{ position: "absolute", top: 8, right: 40 }}
                >
                  <Edit color="primary" />
                </IconButton>

                <Typography variant="subtitle1" fontWeight="bold" mt={1}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {item.description}
                </Typography>
                <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                  {item.treatments.map((treat, i) => (
                    <Chip key={i} label={treat} size="small" />
                  ))}
                </Box>
              </Paper>
            </div>
          ))}
          <div>
            <Paper
              onClick={() => setOpenModal(true)}
              sx={{
                mx: 1,
                height: 200,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                border: "2px dashed #ccc",
              }}
            >
              <Add fontSize="large" />
              <Typography>Add Feature</Typography>
            </Paper>
          </div>
        </Slider>
      </Box>

      {/* Modal for Adding/Editing Feature */}
      <Modal open={openModal} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" mb={2}>
            {currentIndex !== null ? "Edit Feature" : "Add Feature"}
          </Typography>

          <TextField
            label="Title"
            value={newItem.title}
            onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
            fullWidth
            margin="normal"
          />

          <TextField
            label="Description"
            value={newItem.description}
            onChange={(e) =>
              setNewItem({ ...newItem, description: e.target.value })
            }
            fullWidth
            multiline
            rows={3}
            margin="normal"
          />

          <Box display="flex" gap={1} alignItems="center" mt={2}>
            <TextField
              label="Add Treatment"
              value={newTreatment}
              onChange={(e) => setNewTreatment(e.target.value)}
              fullWidth
            />
            <Button onClick={handleAddTreatment} variant="contained">
              Add
            </Button>
          </Box>

          <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
            {newItem.treatments.map((t, idx) => (
              <Chip
                key={idx}
                label={t}
                onDelete={() => handleRemoveTreatment(idx)}
                deleteIcon={<Close />}
              />
            ))}
          </Box>

          <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveItem}
              disabled={
                !newItem.title ||
                !newItem.description ||
                newItem.treatments.length === 0
              }
            >
              {currentIndex !== null ? "Update" : "Add"}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Features;
